import { NodeModel, RenderParams } from "@minoru/react-dnd-treeview";
import { ArrowRight } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import {
	FC,
	MouseEvent as ReactMouseEvent,
	memo,
	useCallback,
	useEffect,
} from "react";

import { FileProperties } from "../models/FileProperties";
import { TreeParameters } from "../models/TreeParameter";
import { TypeIcon } from "./DelayConfiguration/TypeIcon";

type Props = RenderParams & {
	node: NodeModel<FileProperties>;
	setContextMenuOver: (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => void;
	onDoubleClick: (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => void;
	isSelected: boolean;
	isDragging: boolean;
	testIdPrefix?: string;
	onClick: (e: ReactMouseEvent, node: NodeModel<FileProperties>) => void;
	onToggleOpen: (id: number | string) => void;
	"data-node-id"?: string;
};
const CustomNodeInner: FC<Props> = ({
	setContextMenuOver,
	onDoubleClick,
	testIdPrefix = "",
	isSelected,
	...props
}) => {
	const { id, droppable, data } = props.node;
	const indent = props.depth * 24;

	const treeNode = props.node as unknown as TreeParameters;
	const isActive = treeNode.isActive !== false;
	const isQuickButton = treeNode.quickButtonEnabled === true;

	const handleClick = (e: ReactMouseEvent) => {
		props.onClick(e, props.node);
	};

	const handleToggle = (e: ReactMouseEvent) => {
		e.stopPropagation();
		props.onToggleOpen(id);
		props.onToggle();
	};

	const handleContextMenu = useCallback(
		(ev: any) => {
			ev.stopPropagation();
			ev.preventDefault();
			setContextMenuOver(props.node, ev);
		},
		[id, setContextMenuOver],
	);

	const handleViewDelayDialog = useCallback(
		(e: any) => {
			e.stopPropagation();
			e.preventDefault();
			onDoubleClick(props.node, e);
			setContextMenuOver(
				{
					id: "",
					parent: "",
					text: "",
					droppable: false,
				},
				e,
			);
		},
		[id, onDoubleClick],
	);

	useEffect(() => {
		props.containerRef.current?.addEventListener(
			"contextmenu",
			handleContextMenu,
		);
		props.containerRef.current?.addEventListener(
			"dblclick",
			handleViewDelayDialog,
		);

		return () => {
			props.containerRef.current?.removeEventListener(
				"contextmenu",
				handleContextMenu,
			);
			props.containerRef.current?.removeEventListener(
				"dblclick",
				handleViewDelayDialog,
			);
		};
	}, []);

	return (
		<div
			style={{
				paddingInlineStart: indent,
				alignItems: "center",
				display: "flex",
				height: "32px",
				listStyleType: "none",
				backgroundColor: isSelected ? "#e8f0fe" : "",
			}}
			data-node-id={id}
			onClick={handleClick}
			title={`${props.node.id} - ${props.node.text}`}
		>
			{props.node.droppable ? (
				<div
					style={{
						alignItems: "center",
						cursor: "pointer",
						display: "flex",
						height: "24px",
						justifyContent: "center",
						width: "24px",
						transition: "transform linear 0.1s",
						transform: props.isOpen ? "rotate(90deg)" : "rotate(0deg)",
					}}
				>
					<div onClick={handleToggle}>
						<ArrowRight data-testid={`arrow-right-icon-${id}`} />
					</div>
				</div>
			) : null}
			<div
				style={{
					display: "flex",
				}}
			>
				<TypeIcon droppable={droppable as boolean} fileType={data?.fileType} />
			</div>
			<div
				style={{
					paddingInlineStart: "8px",
					display: "flex",
					alignItems: "center",
					gap: "6px",
				}}
			>
				<Typography
					variant="body2"
					sx={{
						color: isActive ? "text.primary" : "text.disabled",
						fontStyle: isActive ? "normal" : "italic",
					}}
				>
					{props.node.text}
				</Typography>
				{isQuickButton && (
					<Box
						sx={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							backgroundColor: "primary.main",
							flexShrink: 0,
						}}
					/>
				)}
			</div>
		</div>
	);
};

export const CustomNode = memo(CustomNodeInner);
